<template>
  <div class="legal-container">
    <!-- esign successful loader -->
    <div
      v-if="loading"
      class="loading-container d-flex justify-content-center align-items-center"
    >
      <div
        class="drip-h3 font-weight-700 color-noble-blue-500 d-flex flex-column justify-content-center"
      >
        {{ $t('legal.page2.loaderText') }}
        <img
          src="@/assets/images/common/green-loader.png"
          class="loader"
          width="64"
          height="64"
        />
      </div>
    </div>
    <!-- view esign instruction -->
    <div v-else class="legal-inner-container">
      <div class="adobe-esign-section" v-if="!esignCompleted">
        <div class="drip-h2">
          {{ $t('legal.page2.mainTitle') }}
        </div>
        <div class="drip-body font-weight-400">
          {{ $t('legal.page2.subtitle') }}
        </div>
        <div class="spacing-4"></div>
        <div class="drip-body">
          {{
            $t('legal.page2.subtext', {
              name: sName
            })
          }}
          <span class="ml-1 mr-2">|</span>
          <a
            :href="`mailto:${sEmail}`"
            class="color-wealthy-green-500 underline-on-hover"
          >
            {{ sEmail }}
          </a>
        </div>
        <div class="spacing-16"></div>
        <div class="d-flex">
          <action-button
            v-if="!esignCompleted"
            variant="outline"
            extraClass="width-260 mr-2 removeVerticalPadding"
            :click="goBack"
          >
            <b-icon-arrow-repeat
              v-if="backBtnLoading"
              class="mr-2"
              animation="spin"
            />
            <b-icon-arrow-left v-else class="mr-2" />
            {{ $t('legal.page2.button') }}
          </action-button>
          <action-button
            v-if="fileUrl"
            variant="text"
            extraClass="width-260 removeVerticalPadding"
            :click="downloadAgreement"
          >
            <template v-if="downloadBtnLoading">
              <b-icon-arrow-repeat class="mr-2" animation="spin" />
              {{ $t('legal.page2.download') }}
            </template>
            <template v-else>
              {{ $t('legal.page2.download') }}

              <img
                src="@/assets/images/common/download-icon.svg"
                class="download-icon"
              />
            </template>
          </action-button>
        </div>
      </div>
      <!-- esign success message -->
      <div id="adobe-esign-section-success" class="adobe-esign-section" v-else>
        <div class="spacing-8"></div>
        <div class="drip-h2 d-flex align-items-center">
          <img
            src="@/assets/images/common/check-circle-outline-green.svg"
            class="mr-2"
          />
          {{ $t('legal.page2.esignSuccess.title') }}
        </div>
        <div class="drip-body font-weight-400">
          {{ $t('legal.page2.esignSuccess.message') }}
        </div>
        <div class="spacing-16"></div>
        <action-button
          v-if="signedFileUrl"
          variant="solid"
          extraClass="width-260 removeVerticalPadding"
          :click="openSignedAgreement"
        >
          {{ $t('legal.page2.download') }}
          <img
            src="@/assets/images/common/download-icon-white.svg"
            class="download-icon"
          />
        </action-button>
      </div>
      <div class="spacing-32"></div>
      <!-- view esign document -->
      <div v-if="esignUrl" class="iframe-container">
        <iframe :src="`${esignUrl}`" width="100%" height="600"></iframe>
      </div>
      <div class="spacing-32"></div>
    </div>
  </div>
</template>
<script>
import ActionButton from '@/components/common/ActionButton'
import exportersService from '@/services/exporters'
import accountService from '@/services/accounts'

export default {
  components: {
    ActionButton
  },
  data() {
    return {
      loading: false,
      fileDownloadLoading: false,
      esignCompleted: false,
      esignUrl: '',
      fileUrl: '',
      sName: '',
      sEmail: '',
      signedFileUrl: null,
      backBtnLoading: false,
      downloadBtnLoading: false
    }
  },
  mounted() {
    if (
      this.$store.state.account &&
      this.$store.state.account.account &&
      this.$store.state.account.account.legal_agreements_esigned
    ) {
      this.$mpTrack('Legal Signing - User forced redirect to dashboard')
      this.$router.push({ name: 'dashboard' })
    }
    this.$mpTrack('Legal Signing - Esign page mounted')
    this.esignUrl = this.$route.query.esign_url
    this.fileUrl = this.$route.query.file_url
    this.sName = this.$route.query.sales_manager_name
    this.sEmail = this.$route.query.sales_manager_email
    window.addEventListener('message', this.eventHandler)
    this.isUserSignedLegal()
  },
  destroyed() {
    window.removeEventListener('message', this.eventHandler)
  },
  methods: {
    async isUserSignedLegal(cb) {
      const res = await accountService.fetchLoggedUser(
        this.$store.getters.token
      )
      const linkedUsers = res && res.data && res.data.linked_users
      const accountId =
        this.$store.state.account && this.$store.state.account.accountId
      const accountIndex =
        linkedUsers && linkedUsers.findIndex((x) => x.id === accountId)
      const isLegalSignedFlag =
        linkedUsers && linkedUsers[accountIndex || 0].legal_agreements_esigned
      if (isLegalSignedFlag) {
        // Refresh user stored in localStorage for browser refresh
        const user = JSON.parse(localStorage.getItem('user'))
        const index = accountIndex || 0
        user.linked_users[index].legal_agreements_esigned = true
        localStorage.setItem('user', JSON.stringify(user))

        // Refresh user status in vue store
        this.$store.dispatch('setEsignStatus', true)

        this.esignCompleted = true

        setTimeout(() => {
          this.$router.push({
            name: 'dashboard',
            query: {
              legalEsign: 'true'
            }
          })
        }, 15000) // 15 seconds
        return
      }
      cb && cb()
    },
    eventHandler(e) {
      const ADOBE_E_SIGN_ORIGIN = 'https://secure.na2.echosign.com'
      if (e.origin === ADOBE_E_SIGN_ORIGIN) {
        console.log('eventHandler', e)
        const data = JSON.parse(e.data)
        if (['PAGE_LOAD'].includes(data.type)) {
          this.$mpTrack('Legal Signing - Event registered!')
        }
        if (['ANALYTICS', 'ERROR'].includes(data.type)) {
          this.$mpTrack('Legal Signing - Error triggered by adobe!', {
            type: data.type
          })
          this.isUserSignedLegal()
        }
        if (data.type === 'ESIGN') {
          this.$mpTrack('Legal Signing - User signed adobe document in iframe')
          this.esignCompleted = true
          document.querySelector('.adobe-esign-section').scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })

          // Refresh user stored in localStorage for browser refresh
          const user = JSON.parse(localStorage.getItem('user'))
          const accountId =
            this.$store.state.account && this.$store.state.account.accountId
          const accountIndex =
            user.linked_users &&
            user.linked_users.findIndex((x) => x.id === accountId)
          const index = accountIndex || 0
          user.linked_users[index].legal_agreements_esigned = true
          localStorage.setItem('user', JSON.stringify(user))

          // Refresh user status in vue store
          this.$store.dispatch('setEsignStatus', true)

          setTimeout(() => {
            this.$router.push({
              name: 'dashboard',
              query: {
                legalEsign: 'true'
              }
            })
          }, 15000) // 15 seconds
        }

        if (data.type === 'REJECT') {
          console.log('Esign rejected', data)
          this.$mpTrack(
            'Legal Signing - User rejected from signing adobe document in iframe'
          )
          this.$notify({
            title: 'Failed',
            text: `Failed to esign document`,
            type: 'error'
          })
        }
      }
    },
    goBack() {
      this.backBtnLoading = true
      const cb = () => {
        const agreementFields = this.$route.query
        this.$mpTrack('Legal Signing - Update date and city CTA')
        this.backBtnLoading = false
        this.$router.push({
          name: 'generateLegal',
          query: agreementFields
        })
      }
      this.isUserSignedLegal(cb)
    },
    downloadAgreement() {
      this.downloadBtnLoading = true
      const cb = () => {
        this.$mpTrack('Legal Signing - Download agreement CTA before Esign')
        if (this.fileUrl) {
          window.open(this.fileUrl, '_blank')
        }
        this.downloadBtnLoading = false
      }
      this.isUserSignedLegal(cb)
    },
    openSignedAgreement() {
      this.$mpTrack('Legal Signing - Download agreement CTA after Esign')
      window.open(this.signedFileUrl, '_blank')
    },
    async downloadSignedAgreement() {
      const res = await exportersService.fetchSignedDocument(
        this.$store.state.account.accountId
      )
      if (res.data.file_url) {
        this.signedFileUrl = res.data.file_url
      }
    },
    async pollDocument() {
      for (let i = 0; i < 3; i++) {
        if (!this.signedFileUrl) {
          await this.downloadSignedAgreement()
        }
        await new Promise((resolve) => setTimeout(resolve, 2000))
      }
    }
  },
  watch: {
    esignCompleted(newVal) {
      if (newVal) {
        this.pollDocument()
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.legal-container {
  margin-top: 94px;
  .legal-inner-container {
    margin: 24px;
    color: $noble-blue-500;
    @media screen and (max-width: $tablet) {
      margin: 12px auto;
      width: 100%;
    }
    .width-260 {
      width: 260px;
    }
    .download-icon {
      width: 40px;
    }
  }
  .loading-container {
    width: 100%;
    height: 89vh;
  }
  .loader {
    margin: auto;
    -webkit-animation: spin 4s linear infinite reverse;
    -moz-animation: spin 4s linear infinite reverse;
    animation: spin 4s linear infinite reverse;
  }
}
@-moz-keyframes spin {
  100% {
    -moz-transform: rotate(360deg);
  }
}
@-webkit-keyframes spin {
  100% {
    -webkit-transform: rotate(360deg);
  }
}
@keyframes spin {
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
</style>
